import axios, { AxiosInstance } from 'axios';
import { UseQueryResult, useQuery } from 'react-query';
import { env } from './common';
import { miningPoolsState, useUserToken } from './state';
import { selector, useRecoilValue } from 'recoil';

interface PoolApiResponse<T> {
  type: string;
  payload: T;
}

interface UserInfo {
  id: string;
  username: string;
  address: string;
  balance: string;
  email: string | null;
  redeemedBalance: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserMiningInfo {
  balance: string;
  endHeight: string;
  startHeight: string;
  totalShareCount: string;
  updatedAt: string;
  userShareCount: string;
}

export class PoolAPI {
  name: string;
  proxy: AxiosInstance;

  constructor(poolname: string, mabelPoolApi: string) {
    this.name = poolname;
    this.proxy = axios.create();
    this.proxy.defaults.baseURL = `${mabelPoolApi}${env.apiBase}`;
    this.proxy.defaults.headers.post['Content-Type'] = 'application/json';
  }

  private usePrivateQuery = (queryKey: any, fetchFn: any) => {
    const jwt = useUserToken(this.name);
    return useQuery(queryKey, () => fetchFn(jwt).then((res: any) => res.data), {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: Boolean(jwt),
    });
  };

  private makeConfig = (jwt: string) => {
    return { headers: { Authorization: `Bearer ${jwt}` } };
  };

  signin = async (username: string, password: string, actionToken: string) => {
    return this.proxy.post('/signin', { username, password, actionToken }, { timeout: 20 * 1000 });
  };

  signup = async (username: string, password: string, actionToken: string) => {
    return this.proxy.post('/signup', { username, password, actionToken });
  };

  getUserProfile = async (jwt: string) => {
    return this.proxy.get<UserInfo>('/user', this.makeConfig(jwt));
  };

  useUserProfile = (uniqueKey?: string): UseQueryResult<PoolApiResponse<UserInfo>> => {
    return this.usePrivateQuery([this.name, 'user-profile', uniqueKey], this.getUserProfile);
  };

  getUserMiningInfos = async (jwt: string) => {
    return this.proxy.get<UserMiningInfo[]>('/user-mining-infos', this.makeConfig(jwt));
  };

  useUserMiningInfos = (uniqueKey?: string): UseQueryResult<PoolApiResponse<UserMiningInfo[]>> => {
    return this.usePrivateQuery([this.name, 'user-mining-infos', uniqueKey], this.getUserMiningInfos);
  };

  getMinedBlockInfos = async (jwt: string) => {
    return this.proxy.get('/mined-block-infos', this.makeConfig(jwt));
  };

  useMinedBlockInfos = () => {
    return this.usePrivateQuery([this.name, 'mined-block-infos'], this.getMinedBlockInfos);
  };

  changeUserAddress = async (jwt: string, value: string) => {
    return this.proxy.post('/address', { value }, this.makeConfig(jwt));
  };

  resetUserAddress = async (jwt: string, username: string) => {
    return this.proxy.post('/reset-address', { username }, this.makeConfig(jwt));
  };
}

export const poolAPIs = selector({
  key: 'poolAPIs',
  get: ({ get }) => {
    const listMiningPools = get(miningPoolsState);
    return Object.fromEntries(
      listMiningPools.map(({ name, mabelPoolApi }) => {
        return [name, new PoolAPI(name, mabelPoolApi)];
      })
    );
  },
});

export function usePoolAPI(poolname?: string) {
  const poolAPIValues = useRecoilValue(poolAPIs);
  return poolAPIValues[poolname ?? env.defaultPoolname];
}

export function useAllPoolAPIs() {
  const poolAPIValues = useRecoilValue(poolAPIs);
  return poolAPIValues;
}
